import React, { useState, useEffect, useCallback } from 'react';
import { PaperAirplaneIcon, TrashIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { config } from '../config/environment';
import { endpoints } from '../config/endpoints';
import { Spinner } from './ui';
import type { Questionnaire } from '../services/pracmagicTypes';

interface Props {
  clientId: string;
}

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
});

const FREQ_LABELS: Record<string, string> = {
  weekly: 'Weekly', biweekly: 'Bi-weekly', monthly: 'Monthly', once: 'One-time',
};

const ClientFormsTab: React.FC<Props> = ({ clientId }) => {
  const [items, setItems] = useState<Questionnaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState<string | null>(null);

  const fetchForms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${config.apiBaseUrl}${endpoints.main.questionnaires.byClient(clientId)}`,
        { headers: authHeaders() }
      );
      const data = await res.json();
      setItems((data.questionnaires || []).filter((q: Questionnaire) => q.source === 'form'));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetchForms(); }, [fetchForms]);

  const send = async (id: string) => {
    setSendingId(id);
    try {
      await fetch(
        `${config.apiBaseUrl}${endpoints.main.questionnaires.sendReminder(id)}`,
        { method: 'POST', headers: authHeaders() }
      );
    } finally {
      setSendingId(null);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Remove this form assignment?')) return;
    await fetch(
      `${config.apiBaseUrl}${endpoints.main.questionnaires.delete(id)}`,
      { method: 'DELETE', headers: authHeaders() }
    );
    setItems(prev => prev.filter(i => i.id !== id));
  };

  if (loading) return <Spinner size="md" className="py-10" />;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-base font-semibold text-white">Forms</h2>
          <p className="text-xs text-gray-500 mt-0.5">Forms assigned to this client</p>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-10 h-10 rounded-full bg-dark-hover flex items-center justify-center mb-3">
            <DocumentTextIcon className="w-5 h-5 text-gray-500" />
          </div>
          <p className="text-sm font-medium text-gray-400">No forms assigned yet</p>
          <p className="text-xs text-gray-600 mt-1">Assign a form from the Forms Library</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(f => (
            <div
              key={f.id}
              className="flex items-center justify-between px-4 py-3 bg-dark-hover border border-dark-border rounded-xl hover:border-gray-600 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{f.title}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {FREQ_LABELS[f.frequency] ?? f.frequency} ·{' '}
                  {f.response_count ?? 0} response{(f.response_count ?? 0) !== 1 ? 's' : ''} ·{' '}
                  {f.next_due_date ? `Due ${f.next_due_date}` : 'No due date'}
                </p>
              </div>
              <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                <button
                  onClick={() => send(f.id)}
                  disabled={sendingId === f.id}
                  title="Send reminder to client"
                  className="text-gray-500 hover:text-primary-400 transition-colors disabled:opacity-40"
                >
                  <PaperAirplaneIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => remove(f.id)}
                  title="Remove"
                  className="text-gray-500 hover:text-red-400 transition-colors"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientFormsTab;
